import { getSDK } from '@/store/sdkStore';
import { eventLog } from '@/lib/eventLog';
import { friendlyError } from '@/lib/errors';

// ============================================================================
// fieldExtensions.ts — ДОПОЛНЕНИЯ ПЛАТФОРМЫ (реестр возможностей ядра; НЕ CFE).
// SSoT — сервер (MetadataService.ListFieldExtensions); клиент только применяет:
// default_for — вид поля по умолчанию (data_type/ref_table/column_name/object).
// Кэш с инвалидацией по событиям сохранения дополнений (дерево/конфигуратор).
// Детерминизм: дополнение не зарегистрировано → вид не применяется (стандартный
// рендер); НЕ найдено по default_for — стандартный рендер (это НЕ фоллбэк:
// дополнение само решило, каким полям оно подходит).
// ============================================================================

export interface FieldExtensionDef {
  code: string;
  name: string;
  kind: string;         // field_view | form_handler | object_handler | command
  oblozhka?: string;    // JSON: {widget, settings_schema}
  logic?: string;       // 'builtin:<code>' | 'module:<code>'
  visibility?: string;  // JSON
  default_for?: string; // JSON: [{data_type,ref_table,column_name,object_type,object_code}]
  isSystem?: boolean;
  templateId?: string;
}

export interface FieldLike {
  dataType?: string;
  data_type?: string;
  refTable?: string;
  ref_table?: string;
  refCatalogCode?: string;
  code?: string;
  name?: string;
  parentType?: string;
  parentCode?: string;
}

/**
 * Код встроенного вида «Применение шаблона отклика» (обработчик — клиентский
 * OfferTemplateButton; используется и в конструкторе форм как явная пометка).
 */
export const OFFER_TEMPLATE_EXTENSION_CODE = 'offer_template';

/**
 * ВСТРОЕННЫЕ ВИДЫ КЛИЕНТА: обработчик живёт в клиенте (пикер шаблонов и
 * подстановка переменных — клиентская логика), поэтому и правила `default_for`
 * задаются рядом с ним; серверный реестр (metadata_field_extensions) — для
 * поставочных/пользовательских дополнений и в приоритете (идёт первым).
 * Правило адресное: поле «Текст отклика» документа отклика (hydra.market) —
 * иначе вид не применяется ни к каким полям (детерминизм).
 */
const CLIENT_BUILTIN_FIELD_VIEWS: FieldExtensionDef[] = [
  {
    code: OFFER_TEMPLATE_EXTENSION_CODE,
    name: 'Применение шаблона отклика',
    kind: 'field_view',
    logic: 'builtin:offer_template',
    default_for: JSON.stringify([
      { object_type: 'document', object_code: 'mkt_responses', column_name: 'comment' },
    ]),
  },
];

let cache: FieldExtensionDef[] | null = null;
let loadPromise: Promise<FieldExtensionDef[]> | null = null;

/** Загрузка реестра дополнений (кэш; ошибка — явная, без фоллбэков). */
export async function loadFieldExtensions(force = false): Promise<FieldExtensionDef[]> {
  if (cache && !force) return cache;
  if (!loadPromise || force) {
    loadPromise = getSDK().metadata.listFieldExtensions({} as any)
      .then((r: any) => {
        // proto (connect-es) отдаёт camelCase: defaultFor — нормализуем в
        // snake_case-алиас интерфейса (SSoT-инвариант реестра дополнений)
        const items = (((r as any)?.items || []) as Record<string, unknown>[]).map((it) => ({
          ...it,
          default_for: (it.default_for ?? it.defaultFor ?? '') as string,
        })) as FieldExtensionDef[];
        if (items.length === 0 && !sessionStorage.getItem('fx-empty-logged')) {
          sessionStorage.setItem('fx-empty-logged', '1');
          void eventLog.warning('ui', 'доп-диаг: ListFieldExtensions вернул 0 записей');
        }
        cache = items;
        return cache;
      })
      .catch((e: unknown) => {
        // Ошибку НЕ глотаем: журнал + сброс promise, чтобы повтор был возможен
        void eventLog.error('ui', 'доп-диаг: ошибка загрузки дополнений', { details: friendlyError(e) });
        loadPromise = null;
        throw e;
      });
  }
  return loadPromise;
}

/** Инвалидация кэша (после сохранения/применения поставки). */
export function invalidateFieldExtensions() {
  cache = null;
  loadPromise = null;
}

interface DefaultForRule {
  data_type?: string;
  ref_table?: string;
  column_name?: string;
  object_type?: string;
  object_code?: string;
}

function parseDefaultFor(raw?: string): DefaultForRule[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Нормализация типа: реквизит ФОРМЫ «string» = текстовое поле платформы «text». */
function normDataType(t: string): string {
  const s = String(t || '').toLowerCase();
  return s === 'string' ? 'text' : s;
}

/** Совпадение поля с правилом default_for (все указанные критерии). */
function ruleMatches(rule: DefaultForRule, field: FieldLike): boolean {
  if (rule.data_type) {
    const dt = normDataType(String(field.dataType || field.data_type || ''));
    if (dt !== normDataType(rule.data_type)) return false;
  }
  if (rule.ref_table) {
    const rt = String(field.refTable || field.ref_table || field.refCatalogCode || '');
    if (rt.toLowerCase() !== rule.ref_table.toLowerCase()) return false;
  }
  if (rule.column_name && String(field.code || field.name || '') !== rule.column_name) return false;
  if (rule.object_type && String(field.parentType || '') !== rule.object_type) return false;
  if (rule.object_code && String(field.parentCode || '') !== rule.object_code) return false;
  return true;
}

/**
 * Дополнение-вид для поля (первый match default_for; только kind='field_view').
 * Порядок: серверный реестр → встроенные клиентские виды. null = дополнение не
 * подходит — стандартный рендер.
 */
export function matchExtensionForField(field: FieldLike): FieldExtensionDef | null {
  const all = cache ? [...cache, ...CLIENT_BUILTIN_FIELD_VIEWS] : CLIENT_BUILTIN_FIELD_VIEWS;
  for (const ext of all) {
    if (ext.kind !== 'field_view') continue;
    for (const rule of parseDefaultFor(ext.default_for)) {
      if (ruleMatches(rule, field)) return ext;
    }
  }
  return null;
}
